import React, { useEffect } from "react";
import { Card, CardBody, CardHeader, Row, Col, Input, Label, FormGroup, Button, Badge, FormFeedback
} from "reactstrap";
import { useFormik } from "formik";
import { financialCategorySchema, financialCategoryDefaults } from "../../../../models/financialCategory";
import PropTypes from "prop-types";
import { OverlayLoader, LoadingButton } from "../../../../components/Common";
import { FINANCIAL_TYPES } from "../../../../constants/financial";
import { GLOBAL_STATUS } from "../../../../constants/common";
import { GLOBAL_STATUS_LABELS } from "../../../../constants/common";;

/**
 * FinancialCategoryEditor
 * Editor visual para Categorias Financeiras (Plano de Contas / DRE).
 * Segue o padrão do LevelEditor com Formik inline.
 */
const FinancialCategoryEditor = ({ category, onSave, onDelete, isSaving, onChange }) => {

    const formik = useFormik({
        initialValues: financialCategoryDefaults,
        validationSchema: financialCategorySchema,
        enableReinitialize: true,
        onSubmit: (values) => {
            onSave(values);
        },
    });

    // Sincroniza estado quando a categoria selecionada muda
    useEffect(() => {
        if (category) {
            formik.resetForm({
                values: {
                    id: category.id || "",
                    name: category.name || "",
                    type: category.type || FINANCIAL_TYPES.INCOME,
                    parentId: category.parentId || null,
                    parentName: category.parentName || "",
                    order: category.order || 0,
                    isSystem: !!category.isSystem,
                    status: category.status || GLOBAL_STATUS.ACTIVE,
                    createdAt: category.createdAt || null,
                    createdBy: category.createdBy || null
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [category]);

    // Notifica mudança para o pai (isDirty)
    useEffect(() => {
        onChange(formik.dirty);
    }, [formik.dirty, onChange]);

    // Estado vazio
    if (!category) {
        return (
            <div className="h-100 d-flex align-items-center justify-content-center bg-light-subtle rounded-0 border border-dashed p-5">
                <div className="text-center">
                    <div className="avatar-lg mx-auto mb-4">
                        <div className="avatar-title bg-primary-subtle text-primary rounded-0 display-4">
                            <i className="mdi mdi-tag-multiple-outline"></i>
                        </div>
                    </div>
                    <h5>Selecione uma categoria</h5>
                    <p className="text-muted" style={{ maxWidth: '300px' }}>
                        Escolha uma categoria na lista ao lado ou crie uma nova para classificar seus lançamentos financeiros.
                    </p>
                </div>
            </div>
        );
    }

    const isSystemCategory = formik.values.isSystem;

    return (
        <Card className="border-0 shadow-sm overflow-hidden rounded-0 position-relative">
            {/* ─── HEADER ─── */}
            <CardHeader className="bg-white py-3 border-bottom sticky-top" style={{ zIndex: 10 }}>
                <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center flex-grow-1 overflow-hidden">
                        <div className="avatar-sm flex-shrink-0">
                            <span className="avatar-title bg-primary bg-gradient text-white rounded-0 shadow-sm">
                                <i className="mdi mdi-tag-multiple-outline font-size-22"></i>
                            </span>
                        </div>
                        <div className="ms-3 overflow-hidden">
                            <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                                <span className="text-truncate">{formik.values.id ? formik.values.name : "Nova Categoria"}</span>
                                {formik.values.id && (
                                    <Badge color={formik.values.status === GLOBAL_STATUS.ACTIVE ? 'success' : 'danger'} pill className="font-size-10 px-2 flex-shrink-0">
                                        {GLOBAL_STATUS_LABELS[formik.values.status] || 'Desconhecido'}
                                    </Badge>
                                )}
                            </h5>
                            <p className="text-muted mb-0 small text-truncate">
                                {formik.values.type === FINANCIAL_TYPES.INCOME ? 'Categoria de Receita' : 'Categoria de Despesa'}
                            </p>
                        </div>
                    </div>
                    <div className="d-flex gap-2 flex-shrink-0">
                        {formik.values.id && !isSystemCategory && (
                            <Button
                                color="light"
                                className="btn-md rounded-0 px-2 shadow-none text-danger border-0 d-flex align-items-center justify-content-center"
                                style={{ width: '38px', height: '38px' }}
                                onClick={() => onDelete(formik.values.id)}
                                disabled={isSaving}
                                title="Excluir Categoria"
                            >
                                <i className="mdi mdi-trash-can-outline font-size-18"></i>
                            </Button>
                        )}
                        <LoadingButton
                            color="primary"
                            className="btn-md rounded-0 px-4 shadow-sm"
                            onClick={formik.handleSubmit}
                            loading={isSaving}
                            loadingText="Salvando..."
                        >
                            Salvar
                        </LoadingButton>
                    </div>
                </div>
            </CardHeader>

            {/* ─── BODY ─── */}
            <CardBody className="p-4">
                <OverlayLoader show={isSaving} label="Salvando..." />
                <Row>
                    <Col lg={12}>
                        <h6 className="text-uppercase font-size-12 fw-bold text-muted mb-4 d-flex align-items-center">
                            <i className="mdi mdi-information-outline me-2 font-size-16 text-primary"></i>
                            Dados da Categoria
                        </h6>
                    </Col>

                    {/* Nome */}
                    <Col md="6">
                        <FormGroup className="mb-3">
                            <Label className="fw-medium text-muted small text-uppercase">
                                Nome da Categoria <span className="text-danger">*</span>
                            </Label>
                            <Input
                                name="name"
                                type="text"
                                className="form-control bg-light-subtle border-0"
                                placeholder="Ex: Mensalidades, Aluguel, Salários..."
                                {...formik.getFieldProps("name")}
                                invalid={formik.touched.name && !!formik.errors.name}
                            />
                            <FormFeedback>{formik.errors.name}</FormFeedback>
                        </FormGroup>
                    </Col>

                    {/* Tipo */}
                    <Col md="3">
                        <FormGroup className="mb-3">
                            <Label className="fw-medium text-muted small text-uppercase">
                                Tipo <span className="text-danger">*</span>
                            </Label>
                            <Input
                                type="select"
                                name="type"
                                className="form-select bg-light-subtle border-0"
                                {...formik.getFieldProps("type")}
                                invalid={formik.touched.type && !!formik.errors.type}
                                disabled={isSystemCategory}
                            >
                                <option value={FINANCIAL_TYPES.INCOME}>💰 Receita</option>
                                <option value={FINANCIAL_TYPES.EXPENSE}>📉 Despesa</option>
                            </Input>
                            <FormFeedback>{formik.errors.type}</FormFeedback>
                        </FormGroup>
                    </Col>

                    {/* Status */}
                    <Col md="3">
                        <FormGroup className="mb-3">
                            <Label className="fw-medium text-muted small text-uppercase">Status</Label>
                            <div className="form-check form-switch pt-2">
                                <Input
                                    type="switch"
                                    role="switch"
                                    checked={formik.values.status === GLOBAL_STATUS.ACTIVE}
                                    onChange={(e) => {
                                        formik.setFieldValue("status", e.target.checked ? GLOBAL_STATUS.ACTIVE : GLOBAL_STATUS.INACTIVE);
                                        onChange(true);
                                    }}
                                    className="form-check-input custom-switch-primary"
                                />
                                <Label className="ms-2 small text-muted">
                                    {GLOBAL_STATUS_LABELS[formik.values.status] || formik.values.status}
                                </Label>
                            </div>
                        </FormGroup>
                    </Col>

                    {/* Ordem */}
                    <Col md="3">
                        <FormGroup className="mb-3">
                            <Label className="fw-medium text-muted small text-uppercase">Ordem na DRE</Label>
                            <Input
                                name="order"
                                type="number"
                                className="form-control bg-light-subtle border-0"
                                placeholder="0"
                                {...formik.getFieldProps("order")}
                            />
                            <small className="text-muted">Define a posição no relatório DRE</small>
                        </FormGroup>
                    </Col>
                </Row>

            </CardBody>
        </Card>
    );
};

FinancialCategoryEditor.propTypes = {
    category: PropTypes.object,
    onSave: PropTypes.func,
    onDelete: PropTypes.func,
    isSaving: PropTypes.bool,
    onChange: PropTypes.func
};

export default FinancialCategoryEditor;
